import { Capacitor } from '@capacitor/core';
import { IonCol, IonIcon, IonImg, IonRow } from '@ionic/react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import * as Sentry from '@sentry/capacitor';
import { cloudDownloadOutline, personCircleOutline } from 'ionicons/icons';
import { useEffect, useState } from 'react';

import { Button } from '@/components/Button';
import {
  RunReportCharts,
  SLSquatReportCharts,
  SquatReportCharts,
  WalkReportCharts,
  createChartRefs,
  chartKeys,
} from '@/components/PDFDownload/Charts';
// import { ChartRefs } from '@/components/PDFDownload/Charts';
import { loadRUNData, loadSLSQUATData, loadSQUATData, loadWALKData } from '@/components/PDFDownload/loadData';
import { PDFVersion } from '@/components/PDFDownload/PDFDocument';
import { Spinner } from '@/components/Spinner/Spinner';

import { mergeClassNames } from '@/utils/mergeClassNames';

import { useOrganizationLogo } from '@/hooks/useOrganizationLogos';

import { useAuthContext } from '@/contexts/authContext';
import { useUserInfoContext } from '@/contexts/userInfoContext';

import ErrorComponent from '@/assets/icons/errorComponentIcon.svg';
import StethoscopeIcon from '@/assets/icons/stethoscope.svg';

import { ReportInfo, ReportType } from '@/models';

import styles from './PDFDownloadModal.module.css';

import { PDFDocument } from '.';
import { CustomLoading } from '../Loading';
import { toast } from '../Toaster/Toast';
import { savePdfToFilesystem } from './PDFDownloadNative';

interface PDFDownloadModalProps {
  //   cleanUp?: () => void;
  PDFError: (error: boolean) => void;
  reportInfo: ReportInfo;
  onClose: () => void;
}

export const PDFDownloadModalContent = ({ PDFError, reportInfo, onClose }: PDFDownloadModalProps) => {
  const userId = useAuthContext().state.userState?.user.uid;
  const { userInfo } = useUserInfoContext();
  const organizationName = userInfo.organization.org_name;
  const [PDFDownloadChoosen, setPDFDownloadChoosen] = useState(false);
  const [PDFVersion, setPDFVersion] = useState<PDFVersion>('Specialist');
  const [dataReady, setDataReady] = useState(false);
  const [dataReceived, setDataReceived] = useState(false);
  const [chartsMounted, setChartsMounted] = useState(false);
  const [PDFDownloadData, setPDFDownloadData] = useState<any>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const reportType =
    reportInfo.reportType === ReportType.WALK
      ? 'Walk'
      : reportInfo.reportType === ReportType.RUN
        ? 'Run'
        : reportInfo.reportType === ReportType.SQUAT
          ? 'Squat'
          : reportInfo.reportType === ReportType.SLSQUAT
            ? 'SLSquat'
            : 'Walk';
  const chartComponentRefs = {
    Walk: createChartRefs(chartKeys.Walk),
    Run: createChartRefs(chartKeys.Run),
    Squat: createChartRefs(chartKeys.Squat),
    SLSquat: createChartRefs(chartKeys.SLSquat),
  };
  const refs = chartComponentRefs[reportType];
  const isNative = Capacitor.isNativePlatform();

  const {
    logoURL: clientLogoURL,
    loading: clientLogoLoading,
    error: clientLogoError,
  } = useOrganizationLogo({
    logoFileName: userInfo.organization.logoURL,
    logoType: 'client',
    asBase64: true,
  });

  const {
    logoURL: headerLogoURL,
    loading: headerLogoLoading,
    error: headerLogoError,
  } = useOrganizationLogo({
    logoFileName: userInfo.organization.logoURL,
    logoType: 'header',
    asBase64: true,
  });

  const isLoadingLogos = clientLogoLoading || headerLogoLoading;
  const hasLogoErrors = clientLogoError || headerLogoError;

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-types
    const loadPDFData = async (loadDataFunction: Function) => {
      try {
        const data = await loadDataFunction(reportInfo, userId!);
        setPDFDownloadData(data);
        setDataReceived(true);
      } catch (error) {
        console.error('Error loading PDF data:', error);
        PDFError(true);
      }
    };

    if (PDFDownloadChoosen) {
      // eslint-disable-next-line @typescript-eslint/ban-types
      const reportLoaders: Record<string, Function> = {
        Walk: loadWALKData,
        Run: loadRUNData,
        Squat: loadSQUATData,
        SLSquat: loadSLSQUATData,
      };

      if (reportType && reportLoaders[reportType]) {
        loadPDFData(reportLoaders[reportType]);
      }
    }
  }, [PDFDownloadChoosen]);

  useEffect(() => {
    if (!dataReceived) return;
    if (!refs) return;

    for (const key in refs) {
      if (refs[key].current === null) {
        return;
      }
    }
    setChartsMounted(true);
  }, [dataReceived, chartComponentRefs, reportType]);

  useEffect(() => {
    if (!chartsMounted) return;
    if (!refs) return;

    const promises = Object.keys(refs).map((key) =>
      // @ts-ignore .chart is not specified in exported type & nullability is checked by chartsMounted
      refs[key].current.chart.dataURI().then(({ imgURI }: { imgURI: string }) => {
        PDFDownloadData[`${key}ChartDataUri`] = imgURI;
      }),
    );

    Promise.all(promises)
      .then(() => setDataReady(true))
      .catch((error) => {
        console.error('Error rendering charts:', error);
        PDFError(true);
      });
  }, [chartsMounted, reportType, PDFDownloadData]);

  useEffect(() => {
    if (hasLogoErrors && dataReady) {
      console.error('Error loading logos:', { clientLogoError, headerLogoError });
      toast({
        variant: 'error',
        title: 'Failed to load logos',
        description: 'Could not load organization logos. Please try again.',
      });
    }
  }, [hasLogoErrors, dataReady, clientLogoError, headerLogoError]);

  const canGeneratePDF = dataReady && !isLoadingLogos && !hasLogoErrors && clientLogoURL && headerLogoURL;
  // Create a filename with the reportInfo.uploadTimestamp (only date part) and reportInfo.reportName
  const uploadDate = new Date(reportInfo.uploadTimestamp);
  const dateString = uploadDate.toISOString().split('T')[0];
  const filename = `${dateString} | ${reportInfo.reportName}`;

  return (
    <>
      <IonRow
        className='ion-justify-content-center ion-align-items-center'
        style={{ height: '200px', marginTop: 'auto', marginBottom: 'auto', position: 'relative' }}>
        <>
          {PDFDownloadChoosen && canGeneratePDF ? (
            isNative ? (
              <div
                className={styles.Download}
                onClick={async () => {
                  setIsLoading(true);
                  try {
                    await savePdfToFilesystem({
                      document: (
                        <PDFDocument
                          clientLogoURL={clientLogoURL}
                          data={PDFDownloadData}
                          headerLogoURL={headerLogoURL}
                          org={organizationName}
                          reportInfo={reportInfo}
                          version={PDFVersion}
                        />
                      ),
                      fileName: filename,
                    });
                    onClose();
                  } catch (error) {
                    Sentry.captureException(error);
                    console.error('Failed to generate and save PDF on native platform', error);
                    toast({
                      variant: 'error',
                      title: 'Failed to save PDF',
                      description: `An error occurred while saving the PDF "${reportInfo.reportName}". Please try again.`,
                    });
                  } finally {
                    setIsLoading(false);
                  }
                }}>
                {isLoading ? (
                  <div className={styles.Loading}>
                    <CustomLoading message='Saving PDF, please wait...' />
                  </div>
                ) : (
                  <>
                    <IonIcon className={styles.DownloadIcon} src={cloudDownloadOutline} />
                    <p className={styles.DownloadLink}>Click to download</p>
                  </>
                )}
              </div>
            ) : (
              <PDFDownloadLink
                document={
                  <PDFDocument
                    clientLogoURL={clientLogoURL}
                    data={PDFDownloadData}
                    headerLogoURL={headerLogoURL}
                    org={organizationName}
                    reportInfo={reportInfo}
                    version={PDFVersion}
                  />
                }
                fileName={`${filename}.pdf`}>
                {({ blob, url, loading, error }) => {
                  if (error) {
                    Sentry.captureException(error);
                  }
                  return !url && !blob && !error ? (
                    <CustomLoading message='Creating PDF, please wait...' />
                  ) : error ? (
                    <div className='' onClick={() => onClose()}>
                      <IonRow className='ion-align-items-stretch ion-no-padding' style={{ padding: '6px' }}>
                        <IonRow className='fullWidth ion-align-items-end'>
                          <IonCol className='' size='7'>
                            <strong>
                              Whoops! Something went wrong. <br />
                            </strong>
                            <p>Sorry about that!</p>
                          </IonCol>
                          <IonCol className='' size='5'>
                            <IonImg src={ErrorComponent} />
                          </IonCol>
                        </IonRow>
                        <IonRow className='fullWidth ion-justify-content-center'>
                          <Button variant='tertiary'>Close</Button>
                        </IonRow>
                      </IonRow>
                    </div>
                  ) : (
                    <div
                      className={styles.Download}
                      onClick={() =>
                        setTimeout(() => {
                          onClose();
                        }, 500)
                      }>
                      <IonIcon className={styles.DownloadIcon} src={cloudDownloadOutline} />
                      <p className={styles.DownloadLink}>Click to download</p>
                    </div>
                  );
                }}
              </PDFDownloadLink>
            )
          ) : PDFDownloadChoosen && !dataReady ? (
            <CustomLoading message='Creating PDF, please wait...' />
          ) : (
            <>
              <IonCol className='' size='6'>
                <IonRow className='ion-justify-content-center'>
                  <IonCol
                    className={mergeClassNames(styles.download_buttonCol, 'ion-text-center')}
                    onClick={() => {
                      setPDFDownloadChoosen(true);
                      setPDFVersion('Specialist');
                    }}
                    size='10'>
                    <IonIcon className={styles.download_buttonIcon} src={StethoscopeIcon} />
                    <h5 className=''>
                      <small>Specialist Report</small>
                    </h5>
                  </IonCol>
                </IonRow>
              </IonCol>
              <IonCol className='' size='6'>
                <IonRow className='ion-justify-content-center'>
                  <IonCol
                    className={mergeClassNames(styles.download_buttonCol, 'ion-text-center')}
                    onClick={() => {
                      setPDFDownloadChoosen(true);
                      setPDFVersion('Client');
                    }}
                    size='10'>
                    <IonIcon className={styles.download_buttonIcon} src={personCircleOutline} />
                    <h5 className=''>
                      <small>Client Report</small>
                    </h5>
                  </IonCol>
                </IonRow>
              </IonCol>
            </>
          )}
        </>
      </IonRow>

      {PDFDownloadChoosen && dataReceived && reportType === 'Walk' && (
        <WalkReportCharts ChartComponentRefs={chartComponentRefs.Walk} PDFDownloadData={PDFDownloadData} />
      )}
      {PDFDownloadChoosen && dataReceived && reportType === 'Run' && (
        <RunReportCharts ChartComponentRefs={chartComponentRefs.Run} PDFDownloadData={PDFDownloadData} />
      )}
      {PDFDownloadChoosen && dataReceived && reportType === 'Squat' && (
        <SquatReportCharts ChartComponentRefs={chartComponentRefs.Squat} PDFDownloadData={PDFDownloadData} />
      )}
      {PDFDownloadChoosen && dataReceived && reportType === 'SLSquat' && (
        <SLSquatReportCharts ChartComponentRefs={chartComponentRefs.SLSquat} PDFDownloadData={PDFDownloadData} />
      )}
    </>
  );
};
